import {
  Assignment as AssignmentIcon,
  EditCalendar as PlanningIcon,
  EventAvailable as MeetingIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import {
  alpha,
  Avatar,
  Box,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import React from "react";
import { SalesOverviewStats } from "../../store/services/salesManagerService";
import {
  CATEGORY_LABELS,
  formatPercent,
  getBacklogTotal,
  getCompletionRateColor,
  getGrandTotal,
  getMotesbokningTotal,
  getPlaneringTotal,
  OVERDUE_BACKLOG_TOOLTIP,
} from "./shared/breakdown";

interface KpiRowProps {
  overview: SalesOverviewStats | undefined;
  isLoading: boolean;
}

interface KpiConfig {
  label: string;
  value: React.ReactNode;
  icon: React.ReactNode;
  accent: string;
  /** Om satt → renderar progressbar + procent istället för caption. */
  rate?: number;
  /** Kort, enradig caption. Används bara om `rate` inte är satt. */
  caption?: string;
  tooltip?: string;
}

const KpiCard: React.FC<{ config: KpiConfig; isLoading: boolean }> = ({
  config,
  isLoading,
}) => {
  const theme = useTheme();
  const card = (
    <Card
      sx={{
        borderRadius: 3,
        boxShadow: `0 2px 12px ${alpha(theme.palette.common.black, 0.06)}`,
        border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
        height: "100%",
        cursor: config.tooltip ? "help" : "default",
      }}
    >
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
          <Avatar
            sx={{
              bgcolor: alpha(config.accent, 0.12),
              color: config.accent,
              width: 48,
              height: 48,
            }}
          >
            {config.icon}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="h5"
              fontWeight="bold"
              sx={{ lineHeight: 1.2 }}
            >
              {isLoading ? <Skeleton width={60} /> : config.value}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {config.label}
            </Typography>
          </Box>
        </Stack>

        {/* CR%-rad med progressbar */}
        {config.rate !== undefined && !isLoading && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ mt: 0.5 }}
          >
            <LinearProgress
              variant="determinate"
              value={config.rate}
              sx={{
                flex: 1,
                height: 6,
                borderRadius: 3,
                bgcolor: alpha(theme.palette.grey[300], 0.3),
                "& .MuiLinearProgress-bar": {
                  borderRadius: 3,
                  bgcolor: config.accent,
                },
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color: config.accent,
                fontWeight: 700,
                minWidth: 38,
                textAlign: "right",
              }}
            >
              {formatPercent(config.rate, 0)}
            </Typography>
          </Stack>
        )}

        {/* Enkel text-caption (endast om ingen rate) */}
        {config.rate === undefined && config.caption && !isLoading && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 0.5 }}
            noWrap
          >
            {config.caption}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  if (!config.tooltip) return card;
  return (
    <Tooltip title={config.tooltip} arrow placement="top">
      {card}
    </Tooltip>
  );
};

const KpiRow: React.FC<KpiRowProps> = ({ overview, isLoading }) => {
  const theme = useTheme();

  const planering = overview
    ? getPlaneringTotal(overview.Breakdown)
    : undefined;
  const motesbokning = overview
    ? getMotesbokningTotal(overview.Breakdown)
    : undefined;
  const totals = overview ? getGrandTotal(overview.Breakdown) : undefined;
  const backlog = overview ? getBacklogTotal(overview.Breakdown) : 0;

  const planeringRate = planering?.CompletionRate ?? 0;
  const motesbokningRate = motesbokning?.CompletionRate ?? 0;

  const configs: KpiConfig[] = [
    {
      label: "Totala aktiviteter",
      value: totals?.Total ?? 0,
      caption: `${totals?.Completed ?? 0} slutförda · ${
        totals?.Active ?? 0
      } aktiva`,
      icon: <AssignmentIcon />,
      accent: theme.palette.info.main,
    },
    {
      label: CATEGORY_LABELS.Communication,
      value: planering?.Total ?? 0,
      rate: planeringRate,
      icon: <PlanningIcon />,
      accent: getCompletionRateColor(planeringRate, theme),
    },
    {
      label: CATEGORY_LABELS.Followup,
      value: motesbokning?.Total ?? 0,
      rate: motesbokningRate,
      icon: <MeetingIcon />,
      accent: getCompletionRateColor(motesbokningRate, theme),
    },
    {
      label: "Overdue + backlog",
      value: backlog,
      caption: `${totals?.Overdue ?? 0} overdue · ${
        overview?.Breakdown.CarriedOverBacklog ?? 0
      } släpas in`,
      icon: <WarningIcon />,
      accent:
        backlog > 0 ? theme.palette.error.main : theme.palette.text.secondary,
      tooltip: OVERDUE_BACKLOG_TOOLTIP,
    },
  ];

  return (
    <Grid container spacing={3} sx={{ mb: 3 }}>
      {configs.map((config) => (
        <Grid item xs={12} sm={6} md={3} key={config.label}>
          <KpiCard config={config} isLoading={isLoading} />
        </Grid>
      ))}
    </Grid>
  );
};

export default KpiRow;
